import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const SERVICES = [
  "Primera visita gratuita",
  "Implantes dentales",
  "Ortodoncia invisible (Invisalign)",
  "Estética dental / Blanqueamiento",
  "Odontopediatría (niños)",
  "Urgencias dentales",
  "Revisión general",
  "Otro",
];

export default function AppointmentForm({ variant = "light", defaultService = "Primera visita gratuita" }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService,
    preferred_date: "",
    message: "",
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    if (!privacyAccepted) {
      toast.error("Debes aceptar la política de privacidad");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const response = await api.post("/appointments", payload);
      console.log("Appointment created:", response.data);
      setSuccess(true);
      toast.success("¡Solicitud enviada! Te contactaremos muy pronto.");
      setForm({ name: "", phone: "", email: "", service: defaultService, preferred_date: "", message: "" });
      setPrivacyAccepted(false);
    } catch (err) {
      console.error("Appointment error:", err);
      const detail = err?.response?.data?.detail;
      let message = "Error al enviar. Inténtalo de nuevo o llámanos.";
      if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0]?.msg || message;
      } else if (typeof detail === "string") {
        message = detail;
      } else if (err?.message) {
        message = err.message;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const labelCls = isDark ? "text-teal-100" : "text-slate-700";
  const inputCls = isDark
    ? "bg-white/10 border-white/20 text-white placeholder:text-teal-200/60 focus:border-white focus:ring-white/20"
    : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-100";

  if (success) {
    return (
      <div className="text-center py-8" data-testid="appointment-success">
        <div className={`w-16 h-16 mx-auto rounded-full ${isDark ? "bg-white/20" : "bg-teal-50"} flex items-center justify-center mb-4`}>
          <CheckCircle2 className={`w-8 h-8 ${isDark ? "text-white" : "text-teal-700"}`} />
        </div>
        <h3 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"} mb-2`}>
          ¡Solicitud recibida!
        </h3>
        <p className={isDark ? "text-teal-100" : "text-slate-600"}>
          Te llamaremos en menos de 24h para confirmar tu cita.
        </p>
        <button
          onClick={() => setSuccess(false)}
          data-testid="new-appointment-button"
          className={`mt-5 text-sm font-medium underline ${isDark ? "text-white" : "text-teal-700"}`}
        >
          Enviar otra solicitud
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="appointment-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={`block text-xs font-semibold mb-1.5 ${labelCls}`}>Nombre*</label>
          <input
            data-testid="appointment-name"
            type="text"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Tu nombre"
            className={`w-full h-12 px-4 rounded-xl border text-sm outline-none transition-colors ${inputCls}`}
          />
        </div>
        <div>
          <label className={`block text-xs font-semibold mb-1.5 ${labelCls}`}>Teléfono*</label>
          <input
            data-testid="appointment-phone"
            type="tel"
            required
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="6XX XXX XXX"
            className={`w-full h-12 px-4 rounded-xl border text-sm outline-none transition-colors ${inputCls}`}
          />
        </div>
      </div>

      <div>
        <label className={`block text-xs font-semibold mb-1.5 ${labelCls}`}>Email (opcional)</label>
        <input
          data-testid="appointment-email"
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="tu@email.com"
          className={`w-full h-12 px-4 rounded-xl border text-sm outline-none transition-colors ${inputCls}`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={`block text-xs font-semibold mb-1.5 ${labelCls}`}>Servicio</label>
          <select
            data-testid="appointment-service"
            value={form.service}
            onChange={(e) => update("service", e.target.value)}
            className={`w-full h-12 px-4 rounded-xl border text-sm outline-none transition-colors ${inputCls}`}
          >
            {SERVICES.map((s) => (
              <option key={s} value={s} className="text-slate-900">{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={`block text-xs font-semibold mb-1.5 ${labelCls}`}>Fecha preferida</label>
          <input
            data-testid="appointment-date"
            type="date"
            value={form.preferred_date}
            onChange={(e) => update("preferred_date", e.target.value)}
            className={`w-full h-12 px-4 rounded-xl border text-sm outline-none transition-colors ${inputCls}`}
          />
        </div>
      </div>

      <div>
        <label className={`block text-xs font-semibold mb-1.5 ${labelCls}`}>Mensaje (opcional)</label>
        <textarea
          data-testid="appointment-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={3}
          placeholder="Cuéntanos en qué podemos ayudarte"
          className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors resize-none ${inputCls}`}
        />
      </div>

      <label className={`flex items-start gap-3 text-xs ${isDark ? "text-teal-100" : "text-slate-600"} cursor-pointer leading-relaxed`}>
        <input
          type="checkbox"
          data-testid="appointment-privacy"
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-orange-600 cursor-pointer shrink-0"
          required
        />
        <span>
          He leído y acepto la{" "}
          <Link
            to="/privacidad"
            target="_blank"
            className={`underline font-medium ${isDark ? "text-white" : "text-teal-700"}`}
          >
            política de privacidad
          </Link>
          {" "}y el tratamiento de mis datos personales para gestionar mi solicitud.*
        </span>
      </label>

      <button
        type="submit"
        disabled={loading}
        data-testid="appointment-submit"
        className={`w-full h-13 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
          isDark
            ? "bg-white text-teal-700 hover:bg-teal-50 py-3.5"
            : "bg-orange-600 text-white hover:bg-orange-700 py-3.5"
        } disabled:opacity-60`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? "Enviando..." : "Pedir mi cita gratuita"}
      </button>

      <p className={`text-xs ${isDark ? "text-teal-200/70" : "text-slate-500"} text-center`}>
        Te contactaremos en menos de 24 horas · Primera visita sin coste
      </p>
    </form>
  );
}
